import * as React from 'react';
import React__default, { useRef, useEffect, useState } from 'react';
import { FaMemory, FaRegImages, FaServer } from 'react-icons/fa';
import { RiArrowDownSFill, RiArrowRightSFill, RiCpuLine, RiCpuFill, RiRhythmLine } from 'react-icons/ri';
import { VscActivateBreakpoints, VscPulse } from 'react-icons/vsc';
import { AiOutlineCodeSandbox } from 'react-icons/ai';
import { FiLayers } from 'react-icons/fi';
import { BsEyeFill, BsEyeSlashFill, BsTriangle } from 'react-icons/bs';
import { IoIosImages, IoIosImage } from 'react-icons/io';
import { IoLayers, IoRocketSharp, IoShapes } from 'react-icons/io5';
import { GoSettings } from 'react-icons/go';
import { useThree, addEffect, addAfterEffect, addTail } from '@react-three/fiber';
import create from 'zustand';
import { Mesh, Points, Texture } from 'three';
import { styled } from '@stitches/react';
import { estimateBytesUsed } from 'three-stdlib';
import { GiPauseButton } from 'react-icons/gi';
import { raf } from 'rafz';
import * as ReactDOM from 'react-dom';

class GLPerf {
  constructor(settings = {}) {
    this.names = [''];
    this.finished = [];
    this.gl = void 0;
    this.extension = void 0;
    this.paused = false;
    this.isWebGL2 = true;
    this.cpuAccums = [];
    this.gpuAccums = [];
    this.activeAccums = [];
    this.chart = [];
    this.gpuChart = [];
    this.cpuChart = [];

    this.paramLogger = () => {};

    this.glFinish = () => {};

    this.chartLogger = () => {};

    this.chartLen = 60;
    this.chartHz = 10;
    this.factorGPU = 1;
    this.trackGPU = true;
    this.trackCPU = false;
    this.chartFrame = 0;
    this.gpuTimeProcess = 0;
    this.chartTime = 0;
    this.activeQueries = 0;
    this.circularId = 0;
    this.detected = 0;
    this.frameId = 0;
    this.paramFrame = 0;
    this.paramTime = 0;

    this.now = () => {};

    this.t0 = 0;
    window.GLPerf = window.GLPerf || {};
    Object.assign(this, settings);
    this.chart = new Array(this.chartLen).fill(0);
    this.gpuChart = new Array(this.chartLen).fill(0);
    this.cpuChart = new Array(this.chartLen).fill(0);

    this.now = () => window.performance && window.performance.now ? window.performance.now() : Date.now();

    this.initGpu();
    this.is120hz();
  }

  initGpu() {
    if (this.gl) {
      this.isWebGL2 = true;
      this.extension = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');

      if (this.extension === null) {
        this.isWebGL2 = false;
      }
    }
  }

  is120hz() {
    let rafId;
    let n = 0;

    const loop = t => {
      if (++n < 20) {
        rafId = window.requestAnimationFrame(loop);
      } else {
        this.detected = Math.ceil(1e3 * n / (t - this.t0) / 70);
        window.cancelAnimationFrame(rafId);
      }

      if (!this.t0) this.t0 = t;
    };

    window.requestAnimationFrame(loop);
  }

  addUI(name) {
    if (this.names.indexOf(name) === -1) {
      this.names.push(name);
      this.cpuAccums.push(0);
      this.gpuAccums.push(0);
      this.activeAccums.push(false);
    }
  }

  nextFrame(now) {
    this.frameId++;
    const t = now || this.now();
    const duration = t - this.paramTime;
    let cpu = 0;
    let gpu = 0;

    if (this.frameId <= 1) {
      this.paramFrame = this.frameId;
      this.paramTime = t;
    } else {
      if (duration >= 500) {
        const frameCount = this.frameId - this.paramFrame;
        const fps = frameCount / duration * 1000;

        for (let i = 0; i < this.names.length; i++) {
          cpu = this.cpuAccums[i] / duration;
          gpu = this.isWebGL2 ? this.gpuAccums[i] : this.gpuAccums[i] / duration;
          const mem = Math.round(window.performance && window.performance.memory ? window.performance.memory.usedJSHeapSize / (1 << 20) : 0);
          this.paramLogger({
            i,
            cpu,
            gpu,
            mem,
            fps: Math.round(fps * 10) / 10,
            duration: Math.round(duration),
            frameCount
          });
          this.cpuAccums[i] = 0;

          if (this.isWebGL2) {
            this.gpuAccums[i] = 0;
          } else {
            Promise.all(this.finished).then(() => {
              this.gpuAccums[i] = 0;
              this.finished = [];
            });
          }
        }

        this.paramFrame = this.frameId;
        this.paramTime = t;
      }
    }

    if (!this.detected || !this.chartFrame) {
      this.chartFrame = this.frameId;
      this.chartTime = t;
      this.circularId = 0;
    } else {
      const timespan = t - this.chartTime;
      let hz = this.chartHz * timespan / 1e3;

      while (--hz > 0 && this.detected) {
        const frameCount = this.frameId - this.chartFrame;
        const fps = frameCount / timespan * 1e3;
        this.chart[this.circularId % this.chartLen] = fps;
        const cpuS = this.cpuAccums[1] / duration + 0.1;
        const gpuS = (this.isWebGL2 ? this.gpuAccums[1] * 2 : Math.round(this.gpuAccums[1] / duration * 100)) + 4;

        if (gpuS > 0) {
          this.gpuChart[this.circularId % this.chartLen] = gpuS;
        }

        if (cpuS > 0) {
          this.cpuChart[this.circularId % this.chartLen] = cpuS;
        }

        for (let i = 0; i < this.names.length; i++) {
          this.chartLogger({
            i,
            data: {
              fps: this.chart,
              gpu: this.gpuChart,
              cpu: this.cpuChart
            },
            circularId: this.circularId
          });
        }

        this.circularId++;
        this.chartFrame = this.frameId;
        this.chartTime = t;
      }
    }
  }

  startGpu() {
    const gl = this.gl;
    const ext = this.extension;
    let query;

    if (this.isWebGL2) {
      query = gl.createQuery();

      if (query instanceof WebGLQuery) {
        gl.beginQuery(ext.TIME_ELAPSED_EXT, query);
      }
    } else {
      return;
    }

    if (!query) {
      return;
    }

    this.activeQueries++;

    const checkQuery = () => {
      if (!query || !this.isWebGL2) {
        return;
      }

      let available, disjoint, ns;

      if (this.isWebGL2) {
        available = gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE);
        disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
        ns = gl.getQueryParameter(query, gl.QUERY_RESULT);
      } else {
        available = ext.getQueryObjectEXT(query, ext.QUERY_RESULT_AVAILABLE_EXT);
        disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
        ns = ext.getQueryObjectEXT(query, ext.QUERY_RESULT_EXT);
      }

      const ms = ns * 1e-6;

      if (available) {
        if (!disjoint) {
          this.activeAccums.forEach((_active, i) => {
            this.gpuAccums[i] = ms;
          });
        }

        this.activeQueries--;
      } else {
        window.requestAnimationFrame(checkQuery);
      }
    };

    window.requestAnimationFrame(checkQuery);
  }

  endGpu() {
    const ext = this.extension;
    const gl = this.gl;

    if (ext === null || !this.isWebGL2) {
      return;
    }

    if (this.isWebGL2 && ext.TIME_ELAPSED_EXT) {
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      gl.flush();
    } else if (ext.TIME_ELAPSED_EXT) {
      ext.endQueryEXT(ext.TIME_ELAPSED_EXT);
      gl.flush();
    }
  }

  begin(name) {
    this.startGpu();
    this.updateAccums(name);
  }

  end(name) {
    this.endGpu();
    this.updateAccums(name);
  }

  updateAccums(name) {
    let nameId = this.names.indexOf(name);

    if (nameId === -1) {
      nameId = this.names.length;
      this.addUI(name);
    }

    const t = this.now();
    const dt = t - this.t0;

    for (let i = 0; i < nameId + 1; i++) {
      if (this.activeAccums[i]) {
        this.cpuAccums[i] += dt;
      }
    }

    this.activeAccums[nameId] = !this.activeAccums[nameId];
    this.t0 = t;
  }

}

const countGeoDrawCalls = programs => {
  programs.forEach((program, _pkey) => {
    const {
      meshes
    } = program;

    if (!meshes) {
      return;
    }

    let drawCounts = {
      total: 0,
      type: 'Triangle',
      data: []
    };
    Object.keys(meshes).forEach(key => {
      const mesh = meshes[key];
      const {
        geometry,
        material
      } = mesh;
      let index = geometry.index;
      const position = geometry.attributes.position;
      let rangeFactor = 1;

      if (material.wireframe === true) {
        rangeFactor = 0;
      }

      const dataCount = index !== null ? index.count : position.count;
      const rangeStart = geometry.drawRange.start * rangeFactor;
      const rangeCount = geometry.drawRange.count * rangeFactor;
      const drawStart = rangeStart;
      const drawEnd = Math.min(dataCount, rangeStart + rangeCount) - 1;
      let countInstanceRatio = 1;
      const instanceCount = mesh.count || 1;
      let type = 'Triangle';
      let mostDrawCalls = 0;

      if (mesh.isMesh) {
        if (material.wireframe === true) {
          type = 'Line';
          countInstanceRatio = countInstanceRatio / 2;
        } else {
          type = 'Triangle';
          countInstanceRatio = countInstanceRatio / 3;
        }
      } else if (mesh.isLine) {
        type = 'Line';

        if (mesh.isLineSegments) {
          countInstanceRatio = countInstanceRatio / 2;
        } else if (mesh.isLineLoop) {
          countInstanceRatio = countInstanceRatio;
        } else {
          countInstanceRatio = countInstanceRatio - 1;
        }
      } else if (mesh.isPoints) {
        type = 'Point';
        countInstanceRatio = countInstanceRatio;
      } else if (mesh.isSprite) {
        type = 'Triangle';
        countInstanceRatio = countInstanceRatio / 3;
      }

      const drawCount = Math.round(Math.max(0, drawEnd - drawStart + 1) * (countInstanceRatio * instanceCount));

      if (drawCount > mostDrawCalls) {
        mostDrawCalls = drawCount;
        drawCounts.type = type;
      }

      drawCounts.total += drawCount;
      drawCounts.data.push({
        drawCount,
        type
      });
      mesh.userData.drawCount = {
        type,
        count: drawCount
      };
    });
    program.drawCounts = drawCounts;
  });
};

const isUUID = uuid => {
  let s = '' + uuid;
  s = s.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');

  if (s === null) {
    return false;
  }

  return true;
};

const addMuiPerfID = (material, currentObjectWithMaterials) => {
  if (!material.defines) {
    material.defines = {};
  }

  if (material.defines && !material.defines.muiPerf) {
    material.defines = Object.assign(material.defines || {}, {
      muiPerf: material.uuid
    });
  }

  const uuid = material.uuid;

  if (!currentObjectWithMaterials[uuid]) {
    currentObjectWithMaterials[uuid] = {
      meshes: {},
      material: material
    };
    material.needsUpdate = true;
  }

  material.needsUpdate = false;
  return uuid;
};

let PerfLib;

const getMUIIndex = muid => muid === 'muiPerf';

const usePerfStore = create(() => ({
  log: null,
  paused: false,
  triggerProgramsUpdate: 0,
  chart: {
    data: {
      fps: [],
      gpu: [],
      cpu: []
    },
    circularId: 0
  },
  gl: undefined,
  objectWithMaterials: null,
  scene: undefined,
  programs: new Map(),
  sceneLength: undefined,
  tab: 'infos'
}));
const usePerfFunc = () => {
  var _usePerfStore;

  return {
    log: usePerfStore(state => state.log),
    gl: (_usePerfStore = usePerfStore(state => state.gl)) === null || _usePerfStore === void 0 ? void 0 : _usePerfStore.info,
    programs: usePerfStore(state => state.programs)
  };
};
const Headless = ({
  trackGPU,
  trackCPU,
  chart
}) => {
  const {
    gl,
    scene
  } = useThree();
  const mounted = useRef(false);
  usePerfStore.setState({
    gl,
    scene
  });
  useEffect(() => {
    gl.info.autoReset = false;

    if (!PerfLib && gl.info) {
      PerfLib = new GLPerf({
        trackGPU: trackGPU,
        trackCPU: trackCPU,
        chartLen: chart ? chart.length : 120,
        chartHz: chart ? chart.hz : 60,
        gl: gl.getContext(),
        chartLogger: chart => {
          usePerfStore.setState({
            chart
          });
        },
        paramLogger: logger => {
          if (PerfLib && gl.info) {
            PerfLib.factorGPU = 1 / gl.info.render.calls;
          }

          usePerfStore.setState({
            log: {
              cpu: logger.cpu,
              gpu: logger.gpu,
              mem: logger.mem,
              fps: logger.fps,
              totalTime: logger.duration,
              frameCount: logger.frameCount
            }
          });
        }
      });
    }

    if (PerfLib && gl.info) {
      PerfLib.gl = gl.getContext();
      const unsub1 = addEffect(() => {
        if (usePerfStore.getState().paused) {
          usePerfStore.setState({
            paused: false
          });
        }

        if (PerfLib && gl.info) {
          gl.info.reset();
          PerfLib.begin('profiler');
        }

        return false;
      });
      const unsub2 = addAfterEffect(() => {
        var _gl$info, _gl$info$programs;

        if (PerfLib) {
          PerfLib.end('profiler');
          PerfLib.nextFrame(window.performance.now());
        }

        const currentObjectWithMaterials = {};
        const programs = new Map();
        scene.traverse(function (object) {
          if (object instanceof Mesh || object instanceof Points) {
            if (object.material) {
              let uuid = object.material.uuid;
              const isTroika = Array.isArray(object.material) && object.material.length > 1;

              if (isTroika) {
                uuid = addMuiPerfID(object.material[1], currentObjectWithMaterials);
              } else {
                uuid = addMuiPerfID(object.material, currentObjectWithMaterials);
              }

              currentObjectWithMaterials[uuid].meshes[object.uuid] = object;
            }
          }
        });
        gl === null || gl === void 0 ? void 0 : (_gl$info = gl.info) === null || _gl$info === void 0 ? void 0 : (_gl$info$programs = _gl$info.programs) === null || _gl$info$programs === void 0 ? void 0 : _gl$info$programs.forEach(program => {
          const cacheKeySplited = program.cacheKey.split(',');
          const muiPerfTracker = cacheKeySplited[cacheKeySplited.findIndex(getMUIIndex) + 1];

          if (isUUID(muiPerfTracker) && currentObjectWithMaterials[muiPerfTracker]) {
            const {
              material,
              meshes
            } = currentObjectWithMaterials[muiPerfTracker];
            programs.set(muiPerfTracker, {
              program,
              material,
              meshes,
              drawCounts: {
                total: 0,
                type: 'triangle',
                data: []
              },
              expand: false,
              visible: true
            });
          }
        });

        if (programs.size !== usePerfStore.getState().programs.size) {
          countGeoDrawCalls(programs);
          usePerfStore.setState({
            programs: programs,
            triggerProgramsUpdate: usePerfStore.getState().triggerProgramsUpdate++
          });
        }

        return false;
      });
      return () => {
        unsub1();
        unsub2();
      };
    } else {
      return undefined;
    }
  }, [gl, trackGPU, trackCPU, chart]);
  useEffect(() => {
    const unsub = addTail(() => {
      if (PerfLib && mounted.current) {
        PerfLib.paused = true;
        usePerfStore.setState({
          paused: true,
          log: {
            cpu: 0,
            gpu: 0,
            mem: 0,
            fps: 0,
            totalTime: 0,
            frameCount: 0
          }
        });
      }

      mounted.current = true;
      return false;
    });
    return () => {
      unsub();
    };
  }, [mounted]);
  return null;
};

const PerfS = styled('div', {
  position: 'fixed',
  top: 0,
  right: 0,
  zIndex: 9999,
  fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif`,
  backgroundColor: 'rgba(36, 36, 36, 1)',
  color: '#fff',
  margin: 0,
  padding: '4px 6px',
  '-webkit-font-smoothing': 'antialiased',
  '-moz-osx-font-smoothing': 'grayscale',
  userSelect: 'none',
  '&.top-left': {
    right: 'initial',
    left: 0
  },
  '&.bottom-left': {
    right: 'initial',
    top: 'initial',
    bottom: 0,
    left: 0,
    '.__perf_toggle': {
      top: '-20px',
      bottom: 'initial'
    }
  },
  '&.bottom-right': {
    top: 'initial',
    bottom: 0,
    '.__perf_toggle': {
      top: '-20px',
      bottom: 'initial'
    }
  },
  '*': {
    margin: '0',
    padding: '0',
    border: '0',
    fontSize: '100%',
    lineHeight: '1',
    verticalAlign: 'baseline'
  }
});
const PerfI = styled('div', {
  display: 'inline-flex',
  fontStyle: 'normal',
  padding: 0,
  lineHeight: '13px',
  fontSize: '14px',
  width: '62px',
  position: 'relative',
  pointerEvents: 'auto',
  cursor: 'default',
  fontWeight: 500,
  letterSpacing: '0px',
  textAlign: 'left',
  height: '29px',
  whiteSpace: 'nowrap',
  justifyContent: 'space-evenly',
  fontVariantNumeric: 'tabular-nums',
  svg: {
    padding: 0,
    color: 'rgba(145, 145, 145, 0.12)',
    fontSize: '22px',
    position: 'absolute',
    zIndex: 1,
    maxHeight: '20px',
    left: ' 50%',
    marginLeft: '-23px',
    top: '4px'
  }
});
const PerfB = styled('span', {
  verticalAlign: 'bottom',
  position: 'absolute',
  bottom: '5px',
  color: 'rgba(101, 197, 188, 1)',
  textAlign: 'right',
  letterSpacing: '1px',
  fontSize: '8px',
  fontWeight: '500',
  width: '60px'
});
const PerfIContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between'
});
const ProgramHeader = styled('div', {
  backgroundColor: '#404040',
  padding: '6px',
  display: 'block',
  fontSize: '12px',
  marginBottom: '6px',
  cursor: 'pointer',
  '*': {
    cursor: 'pointer !important'
  },
  '> span': {},
  small: {
    fontSize: '9px'
  },
  '> b': {
    marginRight: '4px',
    cursor: 'pointer'
  }
});
const Graph = styled('div', {
  height: '61px',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  width: 'auto',
  minWidth: '310px',
  margin: '0 auto'
});
const Graphpc = styled('div', {
  textAlign: 'center',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  verticalAlign: 'middle',
  color: '#f1f1f1',
  padding: '7px',
  width: '100%',
  backgroundColor: 'rgba(36, 36, 37, 0.8)',
  zIndex: 1,
  position: 'absolute',
  height: '100%'
});
const Toggle = styled('div', {
  pointerEvents: 'auto',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '12px',
  backgroundColor: 'rgb(41, 43, 45)',
  marginTop: '6px',
  width: 'auto',
  margin: '0',
  color: 'rgba(145, 145, 145, 1)',
  textAlign: 'center',
  display: 'inline-block',
  verticalAlign: 'middle',
  padding: '4px 6px',
  '&.__perf_toggle_tab_active': {
    backgroundColor: 'rgb(31 31 31)'
  },
  svg: {
    width: '12px',
    height: '12px',
    float: 'left'
  }
});
const ToggleVisible = styled('div', {
  pointerEvents: 'auto',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '12px',
  float: 'right',
  backgroundColor: 'rgb(41, 43, 45)',
  width: 'auto',
  margin: '0',
  color: 'rgba(145, 145, 145, 1)',
  textAlign: 'center',
  display: 'inline-block',
  verticalAlign: 'middle',
  padding: '4px 6px',
  '&.__perf_toggle_tab_active': {
    backgroundColor: 'rgb(31 31 31)'
  },
  svg: {
    width: '12px',
    height: '12px',
    float: 'left'
  }
});
const ProgramGeo = styled('div', {
  padding: '4px 6px',
  fontSize: '12px',
  pointerEvents: 'auto'
});
const ProgramTitle = styled('span', {
  fontWeight: 'bold',
  letterSpacing: '0.08em',
  maxWidth: '145px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  verticalAlign: 'middle',
  fontSize: '11px',
  marginRight: '10px'
});
const ContainerScroll = styled('div', {
  maxHeight: '50vh',
  overflowY: 'auto'
});
const ProgramsULHeader = styled('div', {
  display: 'block',
  position: 'relative',
  fontWeight: 'bold',
  color: '#fff',
  svg: {
    display: 'inline-block'
  }
});
const ProgramsUL = styled('ul', {
  display: 'block',
  position: 'relative',
  paddingLeft: '10px',
  margin: '6px 6px',
  img: {
    maxHeight: '60px',
    maxWidth: '100%',
    margin: '6px auto',
    display: 'block'
  },
  '&:after': {
    content: '',
    position: 'absolute',
    left: '0px',
    top: '0px',
    width: '1px',
    height: '100%',
    backgroundColor: 'grey',
    transform: 'translateX(-50%)',
    maxHeight: '50vh',
    overflowY: 'auto'
  },
  li: {
    borderBottom: '1px solid #313131',
    display: 'block',
    padding: '4px',
    margin: 0,
    lineHeight: 1,
    verticalAlign: 'middle',
    height: '24px'
  },
  b: {
    fontWeight: 'bold'
  },
  small: {
    textAlign: 'revert',
    letterSpacing: '1px',
    fontSize: '10px',
    fontWeight: '500',
    marginLeft: '2px',
    color: 'rgb(101, 197, 188)'
  }
});
const ProgramConsole = styled('button', {
  fontWeight: 'bold',
  letterSpacing: '0.02em',
  backgroundColor: 'rgb(41, 43, 45)',
  color: 'rgb(211, 211, 211)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  display: 'block',
  verticalAlign: 'middle',
  fontSize: '11px',
  padding: '5px',
  margin: '4px auto'
});
const ToggleContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  cursor: 'pointer',
  fontSize: '12px',
  backgroundColor: 'rgb(41, 43, 45)',
  marginTop: '6px',
  width: 'auto',
  margin: '0 auto',
  color: 'rgba(145, 145, 145, 1)',
  textAlign: 'center',
  position: 'absolute',
  right: 0,
  bottom: ' -20px',
  svg: {
    width: '12px',
    height: '12px',
    float: 'left'
  }
});
const ProgramsGeoLi = styled('li', {
  display: 'flex !important',
  height: 'auto !important',
  span: {
    height: '40px',
    display: 'block',
    position: 'relative'
  },
  b: {
    paddingLeft: '12px'
  }
});

const addTextureUniforms = (id, texture) => {
  const repeatType = wrap => {
    switch (wrap) {
      case 1000:
        return 'RepeatWrapping';

      case 1001:
        return 'ClampToEdgeWrapping';

      case 1002:
        return 'MirroredRepeatWrapping';

      default:
        return 'ClampToEdgeWrapping';
    }
  };

  const encodingType = encoding => {
    switch (encoding) {
      case 3000:
        return 'LinearEncoding';

      case 3001:
        return 'sRGBEncoding';

      case 3002:
        return 'RGBEEncoding';

      case 3003:
        return 'LogLuvEncoding';

      case 3004:
        return 'RGBM7Encoding';

      case 3005:
        return 'RGBM16Encoding';

      case 3006:
        return 'RGBDEncoding';

      case 3007:
        return 'GammaEncoding';

      default:
        return 'ClampToEdgeWrapping';
    }
  };

  return {
    name: id,
    url: texture.image.currentSrc,
    encoding: encodingType(texture.encoding),
    wrapT: repeatType(texture.image.wrapT),
    flipY: texture.flipY.toString()
  };
};

const UniformsGL = ({
  program,
  material,
  setTexNumber
}) => {
  const gl = usePerfStore(state => state.gl);
  const [uniforms, set] = useState(null);
  useEffect(() => {
    if (gl) {
      const data = program === null || program === void 0 ? void 0 : program.getUniforms();
      let TexCount = 0;
      const format = new Map();
      data.seq.forEach(e => {
        if (!e.id.includes('uTroika') && e.id !== 'isOrthographic' && e.id !== 'uvTransform' && e.id !== 'lightProbe' && e.id !== 'projectionMatrix' && e.id !== 'viewMatrix' && e.id !== 'normalMatrix' && e.id !== 'modelMatrix' && e.id !== 'modelViewMatrix') {
          let values = [];
          let data = {
            name: e.id
          };

          if (e.cache) {
            e.cache.forEach(v => {
              if (typeof v !== 'undefined') {
                values.push(v.toString().substring(0, 4));
              }
            });
            data.value = values.join();

            if (material[e.id] && material[e.id].image) {
              if (material[e.id].image) {
                TexCount++;
                data.value = addTextureUniforms(e.id, material[e.id]);
              }
            }

            if (!data.value) {
              data.value = 'empty';
            }

            format.set(e.id, data);
          }
        }
      });

      if (material.uniforms) {
        Object.keys(material.uniforms).forEach(key => {
          const uniform = material.uniforms[key];

          if (uniform.value) {
            const {
              value
            } = uniform;
            let data = {
              name: key
            };

            if (key.includes('uTroika')) {
              return;
            }

            if (value instanceof Texture) {
              TexCount++;
              data.value = addTextureUniforms(key, value);
            } else {
              let sb = JSON.stringify(value);

              try {
                sb = JSON.stringify(value);
              } catch (_err) {
                sb = value.toString();
              }

              data.value = sb;
            }

            format.set(key, data);
          }
        });
      }

      if (TexCount > 0) {
        setTexNumber(TexCount);
      }

      set(format);
    }
  }, []);
  return React__default.createElement(ProgramsUL, null, uniforms && Array.from(uniforms.values()).map(uniform => {
    return React__default.createElement("span", {
      key: uniform.name
    }, typeof uniform.value === 'string' ? React__default.createElement("li", null, React__default.createElement("span", null, uniform.name, " :", ' ', React__default.createElement("b", null, uniform.value.substring(0, 30), uniform.value.length > 30 ? '...' : ''))) : React__default.createElement(React__default.Fragment, null, React__default.createElement("li", null, React__default.createElement("b", null, uniform.value.name, ":")), React__default.createElement("div", null, Object.keys(uniform.value).map(key => {
      return key !== 'name' ? React__default.createElement("div", {
        key: key
      }, key === 'url' ? React__default.createElement("a", {
        href: uniform.value[key],
        target: "_blank"
      }, React__default.createElement("img", {
        src: uniform.value[key]
      })) : React__default.createElement("li", null, key, ": ", React__default.createElement("b", null, uniform.value[key]))) : null;
    }), React__default.createElement(ProgramConsole, {
      onClick: () => {
        var _material$uniforms$un;

        console.info(material[uniform.value.name] || (material === null || material === void 0 ? void 0 : (_material$uniforms$un = material.uniforms[uniform.value.name]) === null || _material$uniforms$un === void 0 ? void 0 : _material$uniforms$un.value));
      }
    }, "console.info(", uniform.value.name, ");"))));
  }));
};

const DynamicDrawCallInfo = ({
  el
}) => {
  usePerfStore(state => state.log);
  const gl = usePerfStore(state => state.gl);

  const getVal = el => {
    if (!gl) return 0;
    const res = Math.round(el.drawCounts.total / (gl.info.render.triangles + gl.info.render.lines + gl.info.render.points) * 100 * 10) / 10;
    return isFinite(res) && res || 0;
  };

  return React__default.createElement(React__default.Fragment, null, el.drawCounts.total > 0 && React__default.createElement(PerfI, {
    style: {
      height: 'auto',
      width: 'auto',
      margin: '0 4px'
    }
  }, el.drawCounts.type === 'Triangle' ? React__default.createElement(BsTriangle, {
    style: {
      top: '-1px'
    }
  }) : React__default.createElement(VscActivateBreakpoints, {
    style: {
      top: '-1px'
    }
  }), el.drawCounts.total, React__default.createElement("small", null, el.drawCounts.type, "s"), gl && React__default.createElement(PerfB, {
    style: {
      bottom: '-10px',
      width: '40px',
      fontWeight: 'bold'
    }
  }, el.visible && !el.material.wireframe ? getVal(el) : 0, "%")));
};

const ProgramUI = ({
  el
}) => {
  const [showProgram, setShowProgram] = useState(el.visible);
  const [toggleProgram, set] = useState(el.expand);
  const [texNumber, setTexNumber] = useState(0);
  const {
    meshes,
    program,
    material
  } = el;
  return React__default.createElement(ProgramGeo, null, React__default.createElement(ProgramHeader, {
    onClick: () => {
      el.expand = !toggleProgram;
      Object.keys(meshes).forEach(key => {
        const mesh = meshes[key];
        mesh.material.wireframe = false;
      });
      set(!toggleProgram);
    }
  }, React__default.createElement(Toggle, {
    style: {
      marginRight: '6px'
    }
  }, toggleProgram ? React__default.createElement("span", null, React__default.createElement(RiArrowDownSFill, null)) : React__default.createElement("span", null, React__default.createElement(RiArrowRightSFill, null))), program && React__default.createElement("span", null, React__default.createElement(ProgramTitle, null, program.name), React__default.createElement(PerfI, {
    style: {
      height: 'auto',
      width: 'auto',
      margin: '0 4px'
    }
  }, React__default.createElement(IoLayers, {
    style: {
      top: '-1px'
    }
  }), Object.keys(meshes).length, React__default.createElement("small", null, Object.keys(meshes).length > 1 ? 'users' : 'user')), texNumber > 0 && React__default.createElement(PerfI, {
    style: {
      height: 'auto',
      width: 'auto',
      margin: '0 4px'
    }
  }, texNumber > 1 ? React__default.createElement(IoIosImages, {
    style: {
      top: '-1px'
    }
  }) : React__default.createElement(IoIosImage, {
    style: {
      top: '-1px'
    }
  }), texNumber, React__default.createElement("small", null, "tex")), React__default.createElement(DynamicDrawCallInfo, {
    el: el
  }), material.glslVersion === '300 es' && React__default.createElement(PerfI, {
    style: {
      height: 'auto',
      width: 'auto',
      margin: '0 4px'
    }
  }, React__default.createElement(IoRocketSharp, {
    style: {
      top: '-1px'
    }
  }), "300", React__default.createElement("small", null, "es"), React__default.createElement(PerfB, {
    style: {
      bottom: '-10px',
      width: '40px'
    }
  }, "glsl"))), React__default.createElement(ToggleVisible, {
    onPointerEnter: () => {
      Object.keys(meshes).forEach(key => {
        const mesh = meshes[key];
        mesh.material.wireframe = true;
      });
    },
    onPointerLeave: () => {
      Object.keys(meshes).forEach(key => {
        const mesh = meshes[key];
        mesh.material.wireframe = false;
      });
    },
    onClick: e => {
      e.stopPropagation();
      Object.keys(meshes).forEach(key => {
        const mesh = meshes[key];
        const invert = !showProgram;
        mesh.visible = invert;
        el.visible = invert;
        setShowProgram(invert);
      });
    }
  }, showProgram ? React__default.createElement(BsEyeFill, null) : React__default.createElement(BsEyeSlashFill, null))), React__default.createElement("div", {
    style: {
      maxHeight: toggleProgram ? '9999px' : 0,
      overflow: 'hidden'
    }
  }, React__default.createElement(ProgramsULHeader, null, React__default.createElement(GoSettings, null), " Uniforms:"), React__default.createElement(UniformsGL, {
    program: program,
    material: material,
    setTexNumber: setTexNumber
  }), React__default.createElement(ProgramsULHeader, null, React__default.createElement(IoShapes, null), " Geometries:"), React__default.createElement(ProgramsUL, null, meshes && Object.keys(meshes).map(key => meshes[key] && meshes[key].geometry && React__default.createElement(ProgramsGeoLi, {
    key: key
  }, React__default.createElement("span", null, meshes[key].geometry.type, ": "), meshes[key].userData && meshes[key].userData.drawCount && React__default.createElement("b", null, React__default.createElement("div", null, meshes[key].userData.drawCount.count, React__default.createElement("small", null, " ", meshes[key].userData.drawCount.type, "s")), React__default.createElement("br", null), React__default.createElement("div", null, Math.round(estimateBytesUsed(meshes[key].geometry) / 1024 * 1000) / 1000, "Kb", React__default.createElement("small", null, " memory used")))))), React__default.createElement(ProgramConsole, {
    onClick: () => {
      console.info(material);
    }
  }, "console.info(", material.type, ")")));
};

const ProgramsUI = () => {
  usePerfStore(state => state.triggerProgramsUpdate);
  const programs = usePerfStore(state => state.programs);
  return React__default.createElement(React__default.Fragment, null, programs && Array.from(programs.values()).map(el => {
    if (!el) {
      return null;
    }

    return el ? React__default.createElement(ProgramUI, {
      key: el.material.uuid,
      el: el
    }) : null;
  }));
};

const ChartCurve = ({
  cg,
  canvas,
  colorBlind,
  trackCPU,
  trackGPU
}) => {
  const viewport = {
    x: 0,
    y: 0,
    width: cg.canvas.width,
    height: cg.canvas.height
  };
  const coords = cg.coordinate.cartesian(cg.scale.linear([0, 1], [0, viewport.width - 4]), cg.scale.linear([0, 1], [10, viewport.height - 4]));

  const toPoints = (element, factor = 1) => {
    let maxVal = 0;
    let pointsX = [];
    let pointsY = [];
    const chart = usePerfStore.getState().chart.data[element];

    if (!chart || chart.length === 0) {
      return {
        pointsX: [0],
        pointsY: [0]
      };
    }

    let len = chart.length;

    for (let i = 0; i < len; i++) {
      let id = (usePerfStore.getState().chart.circularId + i + 1) % len;

      if (chart[id] !== undefined) {
        if (chart[id] > maxVal) {
          maxVal = chart[id] * factor;
        }

        pointsX.push(i / (len - 1));
        pointsY.push(Math.min(100, chart[id]) * factor / 100);
      }
    }

    const graph = {
      pointsX,
      pointsY,
      maxVal,
      element
    };
    return graph;
  };

  raf(() => {
    const graphs = [toPoints('fps'), toPoints('cpu')];

    if (trackGPU) {
      graphs.push(toPoints('gpu'));
    }

    const fps = graphs[0];

    const arrData = [cg.lineStrip(fps.pointsX, fps.pointsY, {
      colors: colorBlind ? [100 / 255, 143 / 255, 255 / 255, 1] : [238 / 255, 38 / 255, 110 / 255, 1],
      widths: 1.5
    })];

    if (trackCPU) {
      const cpu = graphs[1];
      cg.lineStrip(cpu.pointsX, cpu.pointsY, {
        colors: colorBlind ? [254 / 255, 254 / 255, 98 / 255, 1] : [66 / 255, 226 / 255, 46 / 255, 1],
        widths: 1.5
      });
    }

    if (trackGPU) {
      const gpu = graphs[2];
      arrData.push(cg.lineStrip(gpu.pointsX, gpu.pointsY, {
        colors: colorBlind ? [254 / 255, 254 / 255, 255 / 255, 1] : [253 / 255, 151 / 255, 31 / 255, 1],
        widths: 1.5
      }));
    }

    cg.clear([0.141, 0.141, 0.141, 1]);
    cg.render(coords, viewport, arrData);
    cg.copyTo(viewport, canvas.current);
    return true;
  });
  return null;
};

const ChartUI = ({
  perfContainerRef,
  colorBlind,
  trackCPU,
  trackGPU
}) => {
  const canvas = useRef(undefined);
  const [cg, setcg] = useState(null);
  useEffect(() => {
    if (canvas.current) {
      import('candygraph').then(module => {
        const CandyGraph = module.CandyGraph;
        const cg = new CandyGraph(canvas.current);
        const {
          width
        } = perfContainerRef.current.getBoundingClientRect();
        cg.canvas.width = width;
        cg.canvas.height = 100;
        setcg(cg);
      });
    }
  }, [canvas.current, perfContainerRef.current]);
  const paused = usePerfStore(state => state.paused);
  return React__default.createElement(Graph, {
    style: {
      display: trackCPU ? 'table' : 'flex'
    }
  }, React__default.createElement("canvas", {
    ref: canvas,
    style: {
      width: `${cg ? cg.canvas.width : 0}px`,
      height: `${cg ? cg.canvas.height : 0}px`,
      marginBottom: `-42px`,
      position: 'relative'
    }
  }), !paused && cg && React__default.createElement(ChartCurve, {
    colorBlind: colorBlind,
    trackCPU: trackCPU,
    trackGPU: trackGPU,
    cg: cg,
    canvas: canvas
  }), paused && React__default.createElement(Graphpc, null, React__default.createElement(GiPauseButton, null), " PAUSED"));
};

function _extends(props, args) {
  return Object.assign(props, args);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

const Html = React.forwardRef((_ref, ref) => {
  var _portal$current;

  let {
    portal,
    className,
    children
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ['portal', 'children', 'className']);

  const {
    gl
  } = useThree(({
    gl
  }) => ({
    gl
  }));
  const [el] = React.useState(() => document.createElement('div'));
  const group = React.useRef(null);
  const target = (_portal$current = portal == null ? void 0 : portal.current) != null ? _portal$current : gl.domElement.parentNode;
  React.useEffect(() => {
    if (group.current) {
      if (target) {
        target.appendChild(el);
      }

      return () => {
        if (target) target.removeChild(el);
        ReactDOM.unmountComponentAtNode(el);
      };
    }

    return undefined;
  }, [target, el]);
  React.useLayoutEffect(() => {
    ReactDOM.render(React.createElement('div', {
      ref: ref,
      className: className,
      children: children
    }), el);
  });
  return React.createElement('group', _extends(props, {
    ref: group
  }));
});

const colorsGraph = colorBlind => {
  const colors = {
    fps: colorBlind ? '100, 143, 255' : '238,38,110',
    cpu: colorBlind ? '254, 254, 98' : '66,226,46',
    gpu: colorBlind ? '254,254,254' : '253,151,31'
  };
  return colors;
};

const DynamicUI = ({
  showGraph,
  trackCPU,
  trackGPU,
  colorBlind
}) => {
  const log = usePerfStore(state => state.log);
  const gl = usePerfStore(state => state.gl);
  return log ? React__default.createElement(PerfIContainer, null, trackCPU && React__default.createElement(PerfI, null, React__default.createElement(RiCpuLine, null), React__default.createElement(PerfB, {
    style: showGraph ? {
      color: `rgb(${colorsGraph(colorBlind).cpu})`
    } : {}
  }, "CPU"), ' ', React__default.createElement("span", null, (Math.round(log.cpu * 100) || 0).toFixed(2), "%")), React__default.createElement(PerfI, null, React__default.createElement(RiCpuFill, null), React__default.createElement(PerfB, {
    style: showGraph && trackGPU ? {
      color: `rgb(${colorsGraph(colorBlind).gpu.toString()})`
    } : {}
  }, "GPU"), ' ', React__default.createElement("span", null, (Math.round(log.gpu * 1000) / 1000 || 0).toFixed(2)), React__default.createElement("small", null, "ms")), React__default.createElement(PerfI, null, React__default.createElement(FaMemory, null), React__default.createElement(PerfB, null, "Memory"), " ", log.mem, React__default.createElement("small", null, "mb")), React__default.createElement(PerfI, null, React__default.createElement(VscPulse, null), React__default.createElement(PerfB, {
    style: showGraph ? {
      color: `rgb(${colorsGraph(colorBlind).fps})`
    } : {}
  }, "FPS"), ' ', React__default.createElement("span", null, log.fps)), gl && React__default.createElement(PerfI, null, React__default.createElement(FiLayers, null), React__default.createElement(PerfB, null, gl.info.render.calls === 1 ? 'call' : 'calls'), ' ', React__default.createElement("span", null, gl.info.render.calls)), gl && React__default.createElement(PerfI, null, React__default.createElement(BsTriangle, null), React__default.createElement(PerfB, null, "Triangles"), ' ', React__default.createElement("span", null, gl.info.render.triangles.toString()))) : null;
};

const PerfUI = ({
  showGraph,
  trackCPU,
  trackGPU,
  colorBlind,
  openByDefault
}) => {
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(DynamicUI, {
    showGraph: showGraph,
    trackGPU: trackGPU,
    trackCPU: trackCPU,
    colorBlind: colorBlind
  }), React__default.createElement(PerfThree, {
    openByDefault: openByDefault
  }));
};

const InfoUI = () => {
  usePerfStore(state => state.log);
  const gl = usePerfStore(state => state.gl);
  if (!gl) return null;
  const {
    info
  } = gl;
  return React__default.createElement("div", null, React__default.createElement(PerfI, null, React__default.createElement(AiOutlineCodeSandbox, null), React__default.createElement(PerfB, null, info.memory.geometries === 1 ? 'Geometry' : 'Geometries'), ' ', React__default.createElement("span", null, info.memory.geometries)), React__default.createElement(PerfI, null, React__default.createElement(FaRegImages, null), React__default.createElement(PerfB, null, info.memory.textures === 1 ? 'Texture' : 'Textures'), ' ', React__default.createElement("span", null, info.memory.textures)), React__default.createElement(PerfI, null, React__default.createElement(FaServer, null), info.programs && React__default.createElement(React__default.Fragment, null, React__default.createElement(PerfB, null, info.programs.length === 1 ? 'shader' : 'shaders'), ' ', React__default.createElement("span", null, info.programs.length))), React__default.createElement(PerfI, null, React__default.createElement(RiRhythmLine, null), React__default.createElement(PerfB, null, "Lines"), " ", React__default.createElement("span", null, info.render.lines)), React__default.createElement(PerfI, null, React__default.createElement(VscActivateBreakpoints, null), React__default.createElement(PerfB, null, "Points"), " ", React__default.createElement("span", null, info.render.points)));
};

const ToggleEl = ({
  tab,
  title,
  set
}) => {
  const tabStore = usePerfStore(state => state.tab);
  return React__default.createElement(Toggle, {
    className: `${tabStore === tab ? ' __perf_toggle_tab_active' : ''}`,
    onClick: () => {
      set(true);
      usePerfStore.setState({
        tab: tab
      });
    }
  }, React__default.createElement("span", null, title));
};

const PerfThree = ({
  openByDefault
}) => {
  const [show, set] = React__default.useState(openByDefault);
  return React__default.createElement("span", null, React__default.createElement(TabContainers, {
    show: show
  }), React__default.createElement(ToggleContainer, {
    className: '__perf_toggle'
  }, React__default.createElement(ToggleEl, {
    tab: "programs",
    title: "Programs",
    set: set
  }), React__default.createElement(ToggleEl, {
    tab: "infos",
    title: "Infos",
    set: set
  }), React__default.createElement(Toggle, {
    onClick: () => {
      set(!show);
    }
  }, show ? React__default.createElement("span", null, React__default.createElement(RiArrowDownSFill, null), " Minimize") : React__default.createElement("span", null, React__default.createElement(RiArrowRightSFill, null), " More"))));
};

const TabContainers = ({
  show
}) => {
  const tab = usePerfStore(state => state.tab);
  return React__default.createElement(React__default.Fragment, null, show && React__default.createElement(ContainerScroll, null, React__default.createElement("div", null, tab === 'programs' && React__default.createElement(ProgramsUI, null), tab === 'infos' && React__default.createElement(InfoUI, null))));
};

const Gui = ({
  showGraph,
  colorBlind,
  trackGPU,
  trackCPU,
  openByDefault,
  className,
  position,
  chart
}) => {
  const perfContainerRef = useRef(null);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Headless, {
    trackGPU: trackGPU,
    trackCPU: trackCPU,
    chart: chart
  }), React__default.createElement(Html, {
    transform: false
  }, React__default.createElement(PerfS, {
    className: (className ? ' '.concat(className) : ' ') + ` ${position ? position : ''}`,
    ref: perfContainerRef
  }, React__default.createElement(PerfUI, {
    colorBlind: colorBlind,
    showGraph: showGraph,
    trackGPU: trackGPU,
    trackCPU: trackCPU,
    openByDefault: openByDefault
  }), showGraph && React__default.createElement(ChartUI, {
    perfContainerRef: perfContainerRef,
    colorBlind: colorBlind,
    trackCPU: trackCPU,
    trackGPU: trackGPU
  }))));
};

const Perf = ({
  headless: _headless = false,
  colorBlind: _colorBlind = false,
  showGraph: _showGraph = true,
  trackCPU: _trackCPU = false,
  trackGPU: _trackGPU = true,
  openByDefault: _openByDefault = false,
  position: _position = 'top-right',
  chart: _chart = {
    length: 30,
    hz: 15
  },
  className
}) => {
  return _headless ? React__default.createElement(Headless, {
    chart: _chart
  }) : React__default.createElement(Gui, {
    colorBlind: _colorBlind,
    showGraph: _showGraph,
    trackCPU: _trackCPU,
    trackGPU: _trackGPU,
    openByDefault: _openByDefault,
    className: className,
    position: _position,
    chart: _chart
  });
};
const usePerf = usePerfFunc;

export { Perf, usePerf };
